import { z } from 'zod';

import {
  CountrySchema,
  PostcodeSchema,
  SchoolNameSchema,
  SearchStringSchema,
} from '../common/schemas';
import { SchoolType } from './enums';

/**
 * DB Schemas
 */

export const DBSchoolSchoolIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9-]+$/)
  .min(1)
  .max(64)
  .openapi({
    description:
      'Unique ID of the school. Seeded schools use public identifiers (aus-prefixed ' +
      'NSW DoE school codes for government schools, enumerated IDs otherwise); ' +
      'manually created schools use a UUID',
    example: 'aus-8132',
  });

export const DBSchoolLabelSchema = SchoolNameSchema.openapi({
  description: 'Official name of the school',
  example: 'North Sydney Boys High School',
});

export const DBSchoolShortLabelSchema = SchoolNameSchema.openapi({
  description:
    "The school's name without generic words such as 'High School', for places with " +
    'little room; no two schools share one. The full name where nothing shorter is free',
  example: 'North Sydney Boys',
});

export const DBSchoolAbbreviationSchema = z
  .string()
  .regex(/^[A-Za-z0-9\-'‘’. ()]+$/)
  .min(2)
  .max(16)
  .openapi({
    description: 'Common abbreviation of the school name',
    example: 'NSB',
  });

export const DBSchoolTypeSchema = z.enum(SchoolType).openapi({
  description: 'The type of the school',
  example: SchoolType.Selective,
});

export const DBSchoolHscRankSchema = z.number().int().min(1).max(500).openapi({
  description: "The school's position in the published NSW HSC success-rate rankings",
  example: 1,
});

export const DBSchoolAddressSchema = z
  .object({
    suburb: z.string().min(1).max(128).optional(),
    state: z.string().min(1).max(64).optional(),
    postcode: PostcodeSchema.optional(),
    country: CountrySchema.optional(),
  })
  .openapi({
    description: 'Address of the school, split into attributes; all attributes are optional',
    example: {
      suburb: 'North Sydney',
      state: 'NSW',
      postcode: '2060',
      country: 'Australia',
    },
  });

export const DBSchoolSearchStrSchema = SearchStringSchema;

export const DBSchoolSchema = z.object({
  schoolId: DBSchoolSchoolIdSchema,
  label: DBSchoolLabelSchema,
  shortLabel: DBSchoolShortLabelSchema,
  abbreviation: DBSchoolAbbreviationSchema.optional(),
  type: DBSchoolTypeSchema,
  hscRank: DBSchoolHscRankSchema.optional(),
  address: DBSchoolAddressSchema,
  searchStr: DBSchoolSearchStrSchema,
});

/**
 * Client Schemas
 */

export const SchoolSchoolIdSchema = DBSchoolSchoolIdSchema;
export const SchoolLabelSchema = DBSchoolLabelSchema;
export const SchoolShortLabelSchema = DBSchoolShortLabelSchema;
export const SchoolAbbreviationSchema = DBSchoolAbbreviationSchema;
export const SchoolTypeSchema = DBSchoolTypeSchema;
export const SchoolHscRankSchema = DBSchoolHscRankSchema;
export const SchoolAddressSchema = DBSchoolAddressSchema;
export const SchoolSearchStrSchema = DBSchoolSearchStrSchema;

export const SchoolSchema = z.object({
  schoolId: SchoolSchoolIdSchema,
  label: SchoolLabelSchema,
  shortLabel: SchoolShortLabelSchema,
  abbreviation: SchoolAbbreviationSchema.optional(),
  type: SchoolTypeSchema,
  hscRank: SchoolHscRankSchema.optional(),
  address: SchoolAddressSchema,
  searchStr: SchoolSearchStrSchema.default(''),
});
